var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState } from 'react';
import { camelCase, snakeCase } from 'lodash';
import { Box, Button, Checkbox, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, LinearProgress, ListItemText, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { tableCase } from '@sierralabs/core';
import { ConfigFieldControl, DragArea, readTextFile } from '@sierralabs/core-ui';
import { useImportEntity } from '../hooks/useImportEntity';
function getRelationLabel(relation) {
    const joinColumn = typeof relation.joinColumn === 'object' && relation.joinColumn.name;
    const joinTable = typeof relation.joinTable === 'object' && relation.joinTable.name;
    const meta = [];
    joinTable && meta.push(joinTable);
    joinColumn && meta.push(joinColumn);
    const all = meta.join(', ');
    return {
        primary: `${relation.displayName} (${relation.propertyName})`,
        secondary: relation.type.concat(all && ` (${all})`)
    };
}
function filterEntity(importEntity, options) {
    // create copy of entity and migration objects
    const entity = Object.assign(Object.assign({}, importEntity), { migration: importEntity.migration && Object.assign({}, importEntity.migration) });
    const excludedRelations = (entity.relations || []).filter(relation => {
        return options.excludedRelations.includes(relation.propertyName);
    });
    if (entity.relations) {
        entity.relations = entity.relations.filter(relation => !excludedRelations.includes(relation));
    }
    // migration
    const { migration } = entity;
    if (migration === null || migration === void 0 ? void 0 : migration.relations) {
        migration.relations = migration.relations.filter(mRelation => {
            const columnName = mRelation.newColumnName || mRelation.columnName;
            const shouldExclude = excludedRelations.some(relation => {
                const joinColumnName = typeof relation.joinColumn === 'object'
                    ? snakeCase(relation.joinColumn.name)
                    : undefined;
                return (mRelation.target === tableCase(relation.target) &&
                    mRelation.type === relation.type &&
                    (!joinColumnName || columnName === joinColumnName));
            });
            return !shouldExclude;
        });
    }
    return entity;
}
export const ImportEntityDialog = props => {
    const { open, onCancel, onImport } = props;
    const onImportRef = useRef(onImport);
    const fileInputRef = useRef(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [options, setOptions] = useState({
        excludedRelations: []
    });
    const [importEntity, setImportEntity] = useState();
    const { importedEntity, isLoading, importError, importEntity: doImportEntity } = useImportEntity();
    const onClose = () => {
        if (!isLoading) {
            onCancel === null || onCancel === void 0 ? void 0 : onCancel();
        }
    };
    const handleFiles = (files) => __awaiter(void 0, void 0, void 0, function* () {
        if (!files || files.length === 0) {
            return;
        }
        const result = yield readTextFile(files[0], value => {
            const schema = JSON.parse(value || '');
            // validate file
            if (!Array.isArray(schema.columns)) {
                throw new Error();
            }
            return schema;
        });
        if (result) {
            const { value, error = '' } = result;
            setImportEntity(value);
            setErrorMessage(error);
            setOptions(options => (Object.assign(Object.assign({}, options), { excludedRelations: [] })));
        }
    });
    const handleSubmit = () => {
        if (!(importEntity === null || importEntity === void 0 ? void 0 : importEntity.name) || isLoading) {
            return;
        }
        const entity = filterEntity(importEntity, options);
        doImportEntity(entity);
    };
    useEffect(() => {
        onImportRef.current = onImport;
    }, [onImport]);
    // reset values
    useEffect(() => {
        if (!open) {
            setImportEntity(undefined);
            setErrorMessage('');
        }
    }, [open]);
    useEffect(() => {
        var _a;
        if (importedEntity) {
            (_a = onImportRef.current) === null || _a === void 0 ? void 0 : _a.call(onImportRef, importedEntity);
        }
    }, [importedEntity]);
    useEffect(() => {
        if (importError) {
            setErrorMessage(importError.message);
        }
    }, [importError]);
    return (React.createElement(Dialog, { fullWidth: true, open: open, onClose: onClose },
        isLoading && React.createElement(LinearProgress, null),
        React.createElement(DialogTitle, null, "Import Entity"),
        React.createElement(DialogContent, null,
            React.createElement(DragArea, { disabled: isLoading, dragLabel: 'Drag exported Entity JSON file to import', buttonLabel: 'Select Import File', onClick: () => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, onUpload: handleFiles }),
            importEntity && (React.createElement(Box, { mt: 2 },
                React.createElement(ImportEntityForm, { options: options, value: importEntity, disabled: isLoading, onSubmit: handleSubmit, onChange: dto => setImportEntity(Object.assign(Object.assign({}, importEntity), dto)), onChangeOptions: updatedOptions => {
                        setOptions(options => (Object.assign(Object.assign({}, options), updatedOptions)));
                    } }))),
            React.createElement(Collapse, { in: !!errorMessage },
                React.createElement(Box, { mt: 2 },
                    React.createElement(Alert, { severity: 'error' }, errorMessage || 'An error occurred.'))),
            React.createElement("input", { hidden: true, type: 'file', accept: 'application/json', ref: fileInputRef, onChange: event => {
                    handleFiles(event.target.files);
                    // clear input field
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                } })),
        React.createElement(DialogActions, null,
            React.createElement(Button, { disabled: isLoading, onClick: onClose }, "Cancel"),
            React.createElement(Button, { color: 'secondary', variant: 'contained', disabled: !(importEntity === null || importEntity === void 0 ? void 0 : importEntity.name) || isLoading, onClick: () => handleSubmit() }, "Import Entity"))));
};
export default ImportEntityDialog;
const ImportEntityForm = props => {
    const { value, options, disabled, onSubmit, onChange, onChangeOptions } = props;
    const relations = Array.isArray(value.relations) ? value.relations : [];
    return (React.createElement(React.Fragment, null,
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { autoFocus: true, fullWidth: true, size: 'small', variant: 'outlined', label: 'Entity Display Name', disabled: disabled, error: !value.displayName, value: value.displayName || '', onKeyPress: event => {
                    if (event.key === 'Enter') {
                        onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit();
                    }
                }, onChange: event => {
                    const displayName = event.target.value;
                    const name = camelCase(displayName);
                    const tableName = tableCase(name);
                    const newValue = {
                        name,
                        displayName,
                        tableName
                    };
                    if (value.migration) {
                        newValue.migration = Object.assign(Object.assign({}, value.migration), { tableName });
                    }
                    onChange(newValue);
                } })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { fullWidth: true, disabled: true, size: 'small', variant: 'outlined', label: 'Entity System Name', value: value.name || '' })),
        value.columns.length > 0 && (React.createElement(Box, { mt: 2, mb: 1 }, "Columns:")),
        value.columns.length > 0 && (React.createElement(Grid, { container: true, spacing: 1 }, value.columns.map((column, index) => (React.createElement(Grid, { item: true, xs: 12, md: 3, key: `${column.name}-${index}` },
            React.createElement(ListItemText, { primary: column.displayName || column.name, secondary: column.type, primaryTypographyProps: { variant: 'body2' } })))))),
        relations.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement(Box, { mt: 2, mb: 2 },
                React.createElement(Divider, null)),
            React.createElement(Box, { mb: 1 }, "Relationships to include:"))),
        relations.length > 0 && (React.createElement(Grid, { container: true, spacing: 1 }, relations.map((relation, index) => (React.createElement(Grid, { item: true, xs: 12, md: 6, key: `${relation.propertyName}-${index}` },
            React.createElement(FormControlLabel, { label: React.createElement(ListItemText, Object.assign({}, getRelationLabel(relation), { primaryTypographyProps: { variant: 'body2' } })), control: React.createElement(Checkbox, { size: 'small', color: 'primary', disabled: disabled, checked: !options.excludedRelations.includes(relation.propertyName), onChange: () => {
                        const excluded = [...options.excludedRelations];
                        const index = excluded.indexOf(relation.propertyName);
                        if (index > -1) {
                            excluded.splice(index, 1);
                        }
                        else {
                            excluded.push(relation.propertyName);
                        }
                        onChangeOptions({ excludedRelations: excluded });
                    } }) }))))))));
};
//# sourceMappingURL=ImportEntityDialog.js.map